import React, { useEffect, useState } from "react";
import {
  Rocket,
  Loader2,
  Linkedin,
  AtSign,
  Eye,
  Send,
  Clock,
  Plus,
  X,
  Mail,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import JobsToDrive from "./JobsToDrive";

const fmtErr = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback || "Something went wrong.";

const CHANNELS = [
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { id: "threads", label: "Threads", Icon: AtSign },
];

const AutoPilot = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [channelStatus, setChannelStatus] = useState({});
  const [stats, setStats] = useState({ usable: null, used: null });
  const [newTime, setNewTime] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const [{ data: cfg }, { data: l }] = await Promise.all([
        api.get("/autopilot/config"),
        api.get("/autopilot/log"),
      ]);
      setConfig(cfg.config);
      setChannelStatus(cfg.channel_status || {});
      setStats(cfg.stats || {});
      setLog(l.items || []);
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't load Auto-Pilot."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/autopilot/config", patch);
      setConfig((c) => ({ ...c, ...data }));
      return true;
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't save."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    const next = !config.enabled;
    const ok = await save({ enabled: next });
    if (ok) toast.success(next ? "Auto-Pilot is ON. Posts will flow automatically." : "Auto-Pilot paused.");
  };

  const toggleChannel = async (id) => {
    const set = new Set(config.channels);
    set.has(id) ? set.delete(id) : set.add(id);
    await save({ channels: [...set] });
  };

  const addTime = async () => {
    if (!/^\d{1,2}:\d{2}$/.test(newTime)) return toast.error("Use HH:MM (e.g. 09:00).");
    const set = new Set(config.times);
    set.add(newTime.padStart(5, "0"));
    const ok = await save({ times: [...set] });
    if (ok) setNewTime("");
  };

  const removeTime = async (t) => {
    const times = config.times.filter((x) => x !== t);
    if (!times.length) return toast.error("Keep at least one time slot.");
    await save({ times });
  };

  const [emailing, setEmailing] = useState(false);
  const toggleDailyEmail = async () => {
    const next = !config.daily_email;
    const ok = await save({ daily_email: next });
    if (ok) toast.success(next ? "Daily email digest ON." : "Daily email digest off.");
  };
  const sendTestEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/promo/email-now", {});
      if (data.status === "sent") toast.success(`Sent to ${data.to} — check your inbox.`);
      else if (data.status === "no_content") toast.error("No eligible articles to email right now.");
      else toast.error(data.detail || "Couldn't send the email.");
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't send the email."));
    } finally {
      setEmailing(false);
    }
  };

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/autopilot/run-now", { dry_run: true });
      if (data.status === "preview") setPreview(data);
      else toast.error(data.detail || "No content available to preview.");
    } catch (e) {
      toast.error(fmtErr(e, "Preview failed."));
    } finally {
      setPreviewing(false);
    }
  };

  const postNow = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/autopilot/run-now", { dry_run: false });
      if (data.status === "no_content") toast.error("No content available.");
      else if (data.status === "failed") toast.error("Publish failed — check your channel connections.");
      else toast.success(data.status === "partial" ? "Posted (some channels failed)." : "Posted live! 🚀");
      load();
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't post."));
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="autopilot-loading">
        <Loader2 className="animate-spin text-cyan-300" />
      </div>
    );
  }

  const noChannelsLive = !CHANNELS.some((c) => channelStatus[c.id]);

  return (
    <div className="space-y-6" data-testid="autopilot-panel">
      {/* Header + master switch */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 flex items-center gap-2">
            <Rocket size={12} /> Auto-Pilot
          </p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">
            Content that ships itself.
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Pulls fresh stories from paisley.org.uk (news, history, attractions, events, food &amp; drink —
            never announcements) and posts them to your channels on a schedule. Even on days you do nothing.
          </p>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={saving}
          data-testid="autopilot-toggle"
          className={`shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-sm font-bold text-xs uppercase tracking-widest transition-colors ${
            config.enabled
              ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF]"
              : "border border-cyan-400/40 text-cyan-300 hover:border-cyan-400"
          }`}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
          {config.enabled ? "Auto-Pilot ON" : "Turn on Auto-Pilot"}
        </button>
      </div>

      {/* Library usage indicator */}
      {stats.usable != null && stats.usable > 0 && (
        <div className="p-4 bg-[#0A192F] border border-white/10 rounded-sm" data-testid="autopilot-usage">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">
              Story library used
            </span>
            <span className="text-sm font-bold text-cyan-300" data-testid="autopilot-usage-count">
              {stats.used} of {stats.usable} used
            </span>
          </div>
          <div className="h-2 w-full bg-[#050B14] rounded-full overflow-hidden border border-white/5">
            <div
              className="h-full bg-[#00F0FF] transition-all duration-500"
              style={{ width: `${Math.min(100, Math.round((stats.used / stats.usable) * 100))}%` }}
              data-testid="autopilot-usage-bar"
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {stats.usable - stats.used > 0
              ? `${stats.usable - stats.used} fresh ${stats.usable - stats.used === 1 ? "story" : "stories"} still to post.`
              : "All caught up — new stories appear as paisley.org.uk publishes them."}
          </p>
        </div>
      )}

      {noChannelsLive && (
        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-400/30 rounded-sm text-amber-200 text-sm" data-testid="autopilot-nochannels">
          <AlertTriangle size={16} /> Connect LinkedIn or Threads first (Connections tab) so posts have somewhere to go.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Channels */}
        <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
          <h3 className="text-sm font-bold text-white mb-3">Publish to</h3>
          <div className="space-y-2">
            {CHANNELS.map(({ id, label, Icon }) => {
              const live = channelStatus[id];
              const on = config.channels.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => live && toggleChannel(id)}
                  disabled={!live || saving}
                  data-testid={`autopilot-channel-${id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border text-sm transition-colors ${
                    on && live
                      ? "border-cyan-400/50 bg-cyan-400/10 text-white"
                      : "border-white/10 text-slate-300 hover:border-white/20"
                  } ${!live ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span className="flex items-center gap-2">
                    <Icon size={16} /> {label}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider">
                    {!live ? "Not connected" : on ? "On" : "Off"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Schedule */}
        <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <Clock size={14} className="text-cyan-300" /> Daily schedule <span className="text-slate-500 font-normal">(UK time)</span>
          </h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {config.times.map((t) => (
              <span
                key={t}
                data-testid={`autopilot-time-${t}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#050B14] border border-white/10 rounded-sm text-sm text-cyan-200 font-mono"
              >
                {t}
                <button onClick={() => removeTime(t)} className="text-slate-500 hover:text-red-300" data-testid={`autopilot-time-remove-${t}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              data-testid="autopilot-time-input"
              className="px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
            />
            <button
              onClick={addTime}
              disabled={saving}
              data-testid="autopilot-time-add"
              className="inline-flex items-center gap-1 px-3 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider"
            >
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Daily email digest */}
      <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm" data-testid="autopilot-email-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Mail size={14} className="text-cyan-300" /> Daily Promo Pack email
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Get the ready-to-paste pack in your inbox each morning at{" "}
              <span className="text-cyan-200 font-mono">{config.daily_email_time || "07:30"}</span> UK
              {config.daily_email_to ? <> · to <span className="text-slate-300">{config.daily_email_to}</span></> : null}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={sendTestEmail}
              disabled={emailing}
              data-testid="autopilot-email-test"
              className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider disabled:opacity-50"
            >
              {emailing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send me one now
            </button>
            <button
              onClick={toggleDailyEmail}
              disabled={saving}
              data-testid="autopilot-email-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors ${
                config.daily_email
                  ? "bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF]"
                  : "border border-cyan-400/40 text-cyan-300 hover:border-cyan-400"
              }`}
            >
              {config.daily_email ? "Email ON" : "Email OFF"}
            </button>
          </div>
        </div>
      </div>

      {/* Preview / post now */}
      <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-sm text-slate-400">
            {stats.usable != null ? (
              <>Library: <span className="text-white font-semibold">{stats.usable}</span> eligible articles · <span className="text-white font-semibold">{stats.used}</span> already used</>
            ) : (
              "Try a live preview of the next post."
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={doPreview}
              disabled={previewing}
              data-testid="autopilot-preview-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-400/40 text-cyan-300 hover:border-cyan-400 rounded-sm text-xs uppercase tracking-wider"
            >
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview next
            </button>
            <button
              onClick={postNow}
              disabled={posting || noChannelsLive}
              data-testid="autopilot-postnow-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-wider hover:bg-[#33F3FF] disabled:opacity-50"
            >
              {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post one now
            </button>
          </div>
        </div>

        {preview && (
          <div className="mt-4 p-4 bg-[#050B14] border border-cyan-400/20 rounded-sm" data-testid="autopilot-preview">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300 mb-1">
              {preview.category} · next up
            </p>
            {preview.image_url && (
              <img
                src={preview.image_url}
                alt={preview.title}
                data-testid="autopilot-preview-image"
                className="w-full max-h-56 object-cover rounded-sm border border-white/10 mb-3"
              />
            )}
            <p className="text-white font-semibold mb-2">{preview.title}</p>
            <p className="text-sm text-slate-300 whitespace-pre-wrap">{preview.blurb}</p>
          </div>
        )}
      </div>

      {/* Recent */}
      <div className="p-5 bg-[#0A192F] border border-white/10 rounded-sm">
        <h3 className="text-sm font-bold text-white mb-3">Recent auto-posts</h3>
        {log.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="autopilot-log-empty">Nothing yet — flip Auto-Pilot on or hit “Post one now”.</p>
        ) : (
          <div className="space-y-2" data-testid="autopilot-log">
            {log.map((row) => (
              <div key={row.log_id} className="flex items-start gap-3 p-3 bg-[#050B14] border border-white/5 rounded-sm">
                {row.status === "published" ? (
                  <CheckCircle2 size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white truncate">{row.title}</p>
                  <p className="text-xs text-slate-500 font-mono">
                    {row.category} · {row.slot} · {Object.keys(row.channels || {}).join(", ") || "—"} · {row.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Daily Jobs → Google Drive automation */}
      <div className="pt-2 border-t border-white/5">
        <JobsToDrive />
      </div>
    </div>
  );
};

export default AutoPilot;
